import React, { createContext, useContext, useState } from 'react';

import { useModal } from 'baukasten';

import { LicenseUpgradeModal } from './LicenseUpgradeModal';
import { LicenseType } from './types';

type LicenseUpgradeModalContextType = {
  openLicenseUpgradeModal: (license: LicenseType) => void;
  closeLicenseUpgradeModal: () => void;
  isLicenseUpgradeModalOpen: boolean;
};

const LicenseUpgradeModalContext =
  createContext<LicenseUpgradeModalContextType | null>(null);

type LicenseUpgradeModalProviderProps = {
  children: React.ReactNode;
};

export const LicenseUpgradeModalProvider = ({
  children,
}: LicenseUpgradeModalProviderProps) => {
  const modal = useModal();

  // Set which license the user wants to upgrade to
  const [upgradeToLicense, setUpgradeToLicense] =
    useState<LicenseType>('standard');

  const openLicenseUpgradeModal = (license: LicenseType) => {
    setUpgradeToLicense(license);
    modal.openModal();
  };

  const contextValue: LicenseUpgradeModalContextType = {
    openLicenseUpgradeModal,
    closeLicenseUpgradeModal: modal.closeModal,
    isLicenseUpgradeModalOpen: modal.isModalOpen,
  };

  return (
    <LicenseUpgradeModalContext.Provider value={contextValue}>
      {children}
      <LicenseUpgradeModal
        isOpen={contextValue.isLicenseUpgradeModalOpen}
        onClose={contextValue.closeLicenseUpgradeModal}
        upgradeToLicense={upgradeToLicense}
      />
    </LicenseUpgradeModalContext.Provider>
  );
};

export const useLicenseUpgradeModal = (): LicenseUpgradeModalContextType => {
  const context = useContext(LicenseUpgradeModalContext);

  if (!context) {
    throw new Error(
      'useLicenseUpgradeModal must be used within a LicenseUpgradeModalProvider'
    );
  }

  return context;
};
